import { observer } from "mobx-react-lite"
import type { ReactNode } from "react"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { FilterableSelect } from "@/components/ui/filterable-select"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { cn } from "@/lib/utils"

export interface SelectOption {
  id: string
  label: string
}

interface PageHeaderProps {
  title: string
  subtitle?: string
  className?: string
  children?: ReactNode
  selectOptions?: SelectOption[]
  selectedOptionId?: string
  onSelectChange?: (optionId: string) => void
  selectPlaceholder?: string
  selectSearchPlaceholder?: string
  selectNoResultsText?: string
  rightContent?: ReactNode
  /** Avatar configuration */
  avatar?: {
    src?: string
    fallback: string
    alt?: string
  }
}

export const PageHeader = observer(
  ({
    title,
    subtitle,
    className,
    children,
    selectOptions,
    selectedOptionId,
    onSelectChange,
    selectPlaceholder = "Select an option",
    selectSearchPlaceholder = "Search...",
    selectNoResultsText = "No results found",
    rightContent,
    avatar,
  }: PageHeaderProps) => {
    const useFilterableSelect = selectOptions && selectOptions.length > 10

    return (
      <div className={cn("space-y-6", className)}>
        {/* Header section with title/subtitle and optional select */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          {/* Title and subtitle with optional avatar */}
          <div className="flex-1 flex items-start gap-3">
            {avatar && (
              <Avatar className="h-10 w-10 sm:h-12 sm:w-12">
                {avatar.src && <AvatarImage src={avatar.src} alt={avatar.alt || title} />}
                <AvatarFallback>{avatar.fallback}</AvatarFallback>
              </Avatar>
            )}
            <div className="flex-1">
              <h1 className="text-2xl sm:text-3xl font-bold mb-2">{title}</h1>
              {subtitle && <p className="text-sm sm:text-base text-muted-foreground">{subtitle}</p>}
            </div>
          </div>

          {/* Right controls section */}
          <div className="flex items-center gap-3">
            {/* Optional select dropdown */}
            {selectOptions &&
              selectOptions.length > 0 &&
              (useFilterableSelect ? (
                <FilterableSelect
                  options={selectOptions}
                  value={selectedOptionId}
                  onValueChange={onSelectChange}
                  placeholder={selectPlaceholder}
                  searchPlaceholder={selectSearchPlaceholder}
                  noResultsText={selectNoResultsText}
                  className="w-full sm:w-[200px]"
                />
              ) : (
                <Select value={selectedOptionId} onValueChange={onSelectChange}>
                  <SelectTrigger
                    className="w-full sm:w-[200px]"
                    aria-label={selectPlaceholder || "Select an option"}
                  >
                    <SelectValue placeholder={selectPlaceholder} />
                  </SelectTrigger>
                  <SelectContent>
                    {selectOptions.map((option) => (
                      <SelectItem key={option.id} value={option.id}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ))}

            {/* Custom right content */}
            {rightContent}
          </div>
        </div>

        {/* Children content */}
        {children}
      </div>
    )
  },
)

PageHeader.displayName = "PageHeader"
